import {
  CdkDrag,
  CdkDragDrop,
  CdkDropList,
  moveItemInArray,
  transferArrayItem,
} from '@angular/cdk/drag-drop';

import { AsyncPipe } from '@angular/common';
import {
  Component,
  computed,
  inject,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  Database,
  get,
  listVal,
  objectVal,
  ref,
  serverTimestamp as rtDbServerTimestamp,
  update,
} from '@angular/fire/database';
import {
  doc,
  Firestore,
  serverTimestamp,
  updateDoc,
} from '@angular/fire/firestore';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { NGXLogger } from 'ngx-logger';
import { map, Observable, of, ReplaySubject, Subject, switchMap, takeUntil, tap } from 'rxjs';
import { AppExceptionModel } from '../../../models/app-exception.model';
import { ProtestantCanonBooks } from '../../../models/books.bible.model';
import { Member } from '../../../models/member.model';
import {
  QuestionType,
  QuizQuestion,
} from '../../../models/question.quiz.model';
import { Quiz, QuizStatus } from '../../../models/quiz.model';
import { QuizSession } from '../../../models/session.quiz.model';
import { UserModel } from '../../../models/user.model';
import { MomentPipe } from '../../../pipes/moment.pipe';
import { QuizService } from '../../../services/quiz.service';
import { UtilsService } from '../../../services/utils.service';
import { AppState } from '../../../state/appstate';
import { BtnActionComponent } from '../../components/buttons/btn-action/btn-action.component';
import { BtnBackComponent } from '../../components/buttons/btn-back/btn-back.component';
import { BtnFabComponent } from '../../components/buttons/btn-fab/btn-fab.component';
import { BibleReferenceRangeFormComponent } from '../../components/inputs/bible-reference-range-form/bible-reference-range-form.component';
import {
  ChipColor,
  ChipComponent,
} from '../../components/inputs/chip/chip.component';
import { QuestionViewComponent } from '../question-bank/question-view/question-view.component';

@Component({
  selector: 'app-live-quiz-room',
  standalone: true,
  imports: [
    BtnBackComponent,
    BtnActionComponent,
    ChipComponent,
    ReactiveFormsModule,
    AsyncPipe,
    BibleReferenceRangeFormComponent,
    CdkDropList,
    CdkDrag,
    BtnActionComponent,
    BtnFabComponent,
    MomentPipe,
    QuestionViewComponent,
  ],
  templateUrl: './live-quiz-room.component.html',
  styleUrl: './live-quiz-room.component.css',
})
export class LiveQuizRoomComponent implements OnInit, OnDestroy {
  store: Store<AppState> = inject(Store<AppState>);
  router = inject(Router);
  private db: Database = inject(Database);
  private firestore: Firestore = inject(Firestore);
  fb: FormBuilder = inject(FormBuilder);
  activatedRoute: ActivatedRoute = inject(ActivatedRoute);
  private logger: NGXLogger = inject(NGXLogger);
  private quizService = inject(QuizService);
  private utilsService = inject(UtilsService);

  groupId: string | null = this.activatedRoute.snapshot.paramMap.get('id')!;
  quizId: string | null = this.activatedRoute.snapshot.paramMap.get('quizId')!;

  user: UserModel | null = null;
  quiz: Quiz | null = null;

  remainingTime = signal(0);
  private timerInterval: any;

  private sessionDbRef = ref(
    inject(Database),
    `sessions/${this.activatedRoute.snapshot.paramMap.get('id')}/${this.activatedRoute.snapshot.paramMap.get('quizId')}`,
  );
  session = toSignal(
    objectVal<QuizSession>(this.sessionDbRef).pipe(
      tap((session) => {
        if (session && session.currentIndex >= 0 && this.quiz) {
          const questionId = this.quiz.questions
            ? this.quiz.questions[session.currentIndex]
            : null;
          if (questionId) {
            // This is what "wakes up" the responses$ observable
            this.activeQuestionId$.next(questionId);

            this.getQuestionById(questionId);
            this.trackResponses(questionId);
            if (session.startTime) {
              this.syncTimer(session.startTime);
            } else {
              // If no startTime exists, it's not "live" yet, reset to full duration
              this.remainingTime.set(this.quiz.answerDuration || 30);
            }
          }
        }
      }),
    ),
    { initialValue: null },
  );

  currentQuestion = computed(() => {
    const s = this.session();
    const allLoadedQuestions = this.selectedQuestions(); // This dependency is key!

    if (!s || s.currentIndex < 0 || !this.quiz?.questions) {
      return null;
    }

    const activeId = this.quiz.questions[s.currentIndex];

    // Find the full question object in our synced signal list
    const found = allLoadedQuestions.find((q) => q.id === activeId);

    if (!found) {
      // If not found, trigger a fetch just in case it was missed
      this.getQuestionById(activeId);
      return null;
    }

    return found;
  });

  students$: Observable<any[]> = of([]);
  onlineStudents$: Observable<any[]> = of([]);
  private activeQuestionId$ = new ReplaySubject<string>(1);

  responses$: Observable<any[]> = this.activeQuestionId$.pipe(
    switchMap((qId) => {
      // We use the snapshot as a fallback if the class variables aren't set yet
      const gid =
        this.groupId || this.activatedRoute.snapshot.paramMap.get('id');
      const qid =
        this.quizId || this.activatedRoute.snapshot.paramMap.get('quizId');

      const path = `responses/${gid}/${qid}/${qId}`;
      this.logger.debug(`Listening for responses at: ${path}`);

      // listVal stays open and pushes new student answers as they arrive
      return listVal<any>(ref(this.db, path));
    }),
    tap((data) =>
      this.logger.debug(`Table Synced: ${data?.length || 0} responses found.`),
    ),
  );

  addQuestions: string[] = [];
  selectedQuestions = signal<QuizQuestion[]>([]);

  isLoading = signal(false);
  isWorking = signal(false);

  chipColor = ChipColor;
  quizStatus = QuizStatus;
  questionTypes = QuestionType;
  protestantBooks = ProtestantCanonBooks;

  totalScore = computed(() => {
    return this.selectedQuestions().reduce((acc, q) => acc + (q.score || 0), 0);
  });

  exit$ = new Observable<void>();
  private destroy$ = new Subject<void>();

  constructor() {}

  ngOnInit(): void {
    this.store
      .select((state) => state.user.user)
      .pipe(takeUntil(this.destroy$))
      .subscribe((user) => {
        this.user = user;
      });
    this.getQuiz();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
    }
  }

  // Refactored Timer to "Sync" rather than just start
  syncTimer(startTime: number) {
    if (this.timerInterval) clearInterval(this.timerInterval);

    const duration = this.quiz?.answerDuration || 30;

    // Create a reusable function to calculate the time
    const updateTimer = () => {
      const elapsedSeconds = Math.floor((Date.now() - startTime) / 1000);
      const timeLeft = Math.max(0, duration - elapsedSeconds);

      this.remainingTime.set(timeLeft);

      if (timeLeft <= 0) {
        if (this.timerInterval) clearInterval(this.timerInterval);
        this.logger.debug('Timer synced to 0 (Expired)');
      }
    };

    // Run it ONCE immediately so the UI doesn't show 30s for a split second
    updateTimer();

    // If there is still time left, start the interval
    if (this.remainingTime() > 0) {
      this.timerInterval = setInterval(updateTimer, 1000);
    }
  }

  getQuiz() {
    if (!this.groupId || !this.quizId) return;

    this.quizService
      .getQuizById({ groupId: this.groupId, quizId: this.quizId })
      .subscribe({
        next: (quiz) => {
          this.quiz = quiz;
          if (this.quiz == null) {
            this.logger.error(
              `Quiz with ID ${this.quizId} not found in group ${this.groupId}`,
            );
            this.utilsService.onShowAlert({
              code: 'QUIZ_NOT_FOUND',
              title: 'Quiz Not Found',
              message: 'The requested quiz could not be found.',
            } as AppExceptionModel);
            this.router.navigate(['/school', this.groupId, 'quizzes']);
          } else {
            this.initListeners();

            const s = this.session();
            if (s && s.currentIndex >= 0) {
              const currentId = this.quiz.questions
                ? this.quiz.questions[s.currentIndex]
                : null;
              if (currentId) {
                this.activeQuestionId$.next(currentId);
                this.getQuestionById(currentId);
              }
            } else if ((this.quiz.questions ?? [])?.length > 0) {
              // Only load the first question if the quiz hasn't started yet
              this.getQuestionById(this.quiz.questions![0]);
              this.remainingTime.set(this.quiz.answerDuration || 30);
            }
          }
        },
        error: (error) => {
          this.logger.error('Error fetching quiz: ', error);
          this.utilsService.onShowAlert(
            {
              code: 'FETCH_QUIZ_ERROR',
              title: 'Error Fetching Quiz',
              message: 'Failed to load quiz. Please try again later.',
            } as AppExceptionModel,
            false,
          );
        },
      });
  }

  async getQuestionById(questionId: string): Promise<void> {
    this.logger.debug(`Fetching question by ID: ${questionId}`);

    const alreadyLoaded = this.selectedQuestions().some(
      (q) => q.id === questionId,
    );
    if (alreadyLoaded) return;

    this.quizService.getQuestionById(this.groupId!, questionId).subscribe({
      next: (question) => {
        if (question) {
          this.logger.debug(`Successfully fetched: ${question.id}`);

          // Use the signal's update method for cleaner immutability
          this.selectedQuestions.update((current) => {
            const index = current.findIndex((q) => q.id === questionId);

            if (index !== -1) {
              // Replace the "shell" ID-only object with the full data
              const updated = [...current];
              updated[index] = question;
              return updated;
            } else {
              // If it wasn't in the list yet, add it
              return [...current, question];
            }
          });
        }
      },
      error: (err: any) => this.logger.error('Fetch failed', err),
    });
  }



  initListeners() {
    if (!this.groupId || !this.quizId) return;

    const presencePath = ref(
      this.db,
      `presence/${this.groupId}/${this.quizId}`,
    );
    this.students$ = listVal(presencePath, { keyField: 'id' });
    this.onlineStudents$ = this.students$.pipe(
      map((students) => (students || []).filter((s) => s.status === 'online')),
    );
  }

  // Helper to switch the live listener for student answers
  trackResponses(questionId: string) {
    if (questionId) {
      this.activeQuestionId$.next(questionId);
    }
  }

  startQuiz() {
    if (
      !this.groupId ||
      !this.quizId ||
      this.quiz?.createdById != this.user?.uid
    )
      return;

    updateDoc(
      doc(this.firestore, `groups/${this.groupId}/quizzes/${this.quizId}`),
      {
        quizStatus: QuizStatus.inProgress,
      },
    )
      .then(() => {
        this.logger.debug('Quiz status updated to inProgress in Firestore');
      })
      .catch((error) => {
        this.logger.error('Failed to update quiz status in Firestore', error);
      });

    const sessionRef = ref(this.db, `sessions/${this.groupId}/${this.quizId}`);

    // This triggers the Flutter apps to switch from "Waiting" to "Question 1"
    update(sessionRef, {
      status: QuizStatus.inProgress,
      questionId: this.quiz?.questions ? this.quiz?.questions[0] || null : null,
      currentIndex: 0,
      durationPerQuestion: this.quiz?.answerDuration || 30,
      totalScore: this.quiz?.totalScore,
      totalQuestions: this.quiz?.questions?.length || 0,
      startTime: rtDbServerTimestamp(),
    })
      .then(() => {
        this.logger.debug('Quiz started - First question pushed to students');
      })
      .catch((error) => {
        this.logger.error('Failed to start quiz in Realtime Database', error);
      });
  }

  async gradeResponse(response: any, teacherSaysCorrect: boolean) {
    const s = this.session();
    const q = this.currentQuestion();

    if (!q || !q.id || !s) {
      this.logger.error('Cannot grade: Question or Session data missing.');
      return;
    }

    // 1. Determine the points for this question
    const maxPoints = q.score || 10;

    // 2. Calculate the Delta
    // response.score is what is currently in the DB (e.g., 10 for auto-correct, 0 for wrong/pending)
    const currentScoreInTable = response.score || 0;
    const newScore = teacherSaysCorrect ? maxPoints : 0;

    // If current is 10 and new is 0, delta is -10.
    // If current is 0 and new is 10, delta is +10.
    const pointDelta = newScore - currentScoreInTable;

    const respPath = `responses/${this.groupId}/${this.quizId}/${q.id}/${response.studentId}`;

    try {
      await Promise.all([
        // Update individual answer status with the new decision
        update(ref(this.db, respPath), {
          isCorrect: teacherSaysCorrect,
          score: newScore,
        }),
        // Pass the DELTA (positive or negative) to the leaderboard
        this.updateCumulativeScore(response.studentId, pointDelta),
      ]);

      this.logger.debug(
        `Graded ${response.studentName}: ${teacherSaysCorrect ? 'Correct' : 'Incorrect'} (Delta: ${pointDelta})`,
      );
    } catch (error) {
      this.logger.error('Grading failed', error);
    }
  }

  private async updateCumulativeScore(studentId: string, pointsToAdd: number) {
    if (pointsToAdd === 0) return; // No need to update if they got it wrong

    const presenceRef = ref(
      this.db,
      `presence/${this.groupId}/${this.quizId}/${studentId}`,
    );

    // Use a transaction or a simple get-then-set
    const snapshot = await get(presenceRef);
    if (snapshot.exists()) {
      const currentData = snapshot.val();
      const newTotal = (currentData.userScore ?? currentData.totalScore ?? 0) + pointsToAdd;

      await update(presenceRef, {
        userScore: newTotal,
        totalScore: newTotal,
      });
    }
  }

  async nextQuestion() {
    const s = this.session();
    if (!s || !this.quiz) return;

    const nextIdx = s.currentIndex + 1;
    if (nextIdx >= (this.quiz.questions?.length || 0)) {
      this.logger.info('End of quiz reached');
      this.utilsService.onShowAlert({
        code: 'NO_MORE_QUESTIONS',
        title: 'End of Quiz',
        message: 'No more questions in the list.',
      } as AppExceptionModel);
      return;
    }

    const nextQuestionId = this.quiz.questions
      ? this.quiz.questions[nextIdx] || null
      : null;
    // Pre-fetch next question to avoid UI flicker
    if (nextQuestionId) await this.getQuestionById(nextQuestionId);

    const sessionRef = ref(this.db, `sessions/${this.groupId}/${this.quizId}`);
    await update(sessionRef, {
      questionId: nextQuestionId,
      currentIndex: nextIdx,
      displayProgress: nextIdx + 1,
      startTime: rtDbServerTimestamp(),
    });
  }

  startTimer(startTime: number) {
    if (this.timerInterval) clearInterval(this.timerInterval);

    const duration = this.quiz?.answerDuration || 30; // Default 30s

    this.timerInterval = setInterval(() => {
      // We calculate based on current time vs. the start time in DB
      const elapsedSeconds = Math.floor((Date.now() - startTime) / 1000);
      const timeLeft = Math.max(0, duration - elapsedSeconds);

      this.remainingTime.set(timeLeft);

      if (timeLeft <= 0) {
        clearInterval(this.timerInterval);
        this.logger.debug('Time is up for current question');
      }
    }, 1000);
  }

  selectedQuestionByIndex(index: number) {
    if (!this.quiz || !this.quiz.questions) return null;
    const qId = this.quiz.questions[index] || null;
    if (qId == null) return null;

    return this.selectedQuestions().find((q) => q.id === qId) || null;
  }

  async endQuiz() {
    if (!this.groupId || !this.quizId) return;

    this.isWorking.set(true);
    this.logger.debug('Ending quiz session: ', this.quizId);

    let step = 'Realtime Database';
    try {
      // Update Realtime Database (Tells Flutter apps to show "Game Over")
      const sessionRef = ref(
        this.db,
        `sessions/${this.groupId}/${this.quizId}`,
      );
      await update(sessionRef, {
        status: QuizStatus.completed,
        endTime: rtDbServerTimestamp(),
      });

      step = 'Firestore';
      // Update Firestore (Permanent record)
      // Assuming your quizService has an update method or use direct Firestore
      const quizDocRef = doc(
        this.firestore,
        'groups',
        this.groupId,
        'quizzes',
        this.quizId,
      );
      await updateDoc(quizDocRef, {
        quizStatus: QuizStatus.completed,
        modified: serverTimestamp(),
      });

      this.logger.debug('Quiz marked as finished in both databases');

      this.quiz!.quizStatus = QuizStatus.completed;
    } catch (error: any) {
      this.logger.error(`Error ending quiz at step ${step}: `, error);
      this.utilsService.onShowAlert(
        {
          code: 'END_QUIZ_ERROR',
          title: 'Error Ending Quiz',
          message: `Failed to close the quiz session properly at step: ${step}. Error: ${error?.message || error || 'Unknown error'}`,
        } as AppExceptionModel,
        false,
      );
    } finally {
      this.isWorking.set(false);
    }
  }

  drop(event: CdkDragDrop<QuizQuestion[]>) {
    if (event.previousContainer === event.container) {
      // Reorder same list
      moveItemInArray(
        event.container.data,
        event.previousIndex,
        event.currentIndex,
      );
    } else {
      // Transfer between lists
      transferArrayItem(
        event.previousContainer.data,
        event.container.data,
        event.previousIndex,
        event.currentIndex,
      );
    }
    this.selectedQuestions.set([...this.selectedQuestions()]);
  }

  async addQuestionToQuiz(question: QuizQuestion) {
    if (!this.quiz) return;

    // 1. Update local Firestore model
    const updatedQuestionIds = [...(this.quiz.questions || []), question.id];

    // 2. Add to your loaded questions signal
    this.selectedQuestions.update((current) => [...current, question]);

    try {
      // 3. Persist to Firestore immediately
      const quizDocRef = doc(
        this.firestore,
        'groups',
        this.groupId!,
        'quizzes',
        this.quizId!,
      );
      await updateDoc(quizDocRef, {
        questions: updatedQuestionIds,
        modified: serverTimestamp(),
      });

      // 4. Update the local quiz object so the UI/Next logic sees the new length
      this.quiz.questions = updatedQuestionIds;

      this.logger.debug('Question added mid-session and synced to Firestore');
    } catch (error) {
      this.logger.error('Failed to sync new question to Firestore', error);
    }
  }

  copyInviteCode() {
    navigator.clipboard.writeText(this.quiz?.inviteCode!);
  }
}
